import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";

const MAX_SIDE = 480;   // small JPEG: enough to recognise the person, cheap to upload and store

// Full-screen front camera: one tap takes the selfie and hands a JPEG data URL to onResult.
export default function SelfieCapture({ title, hint, onResult, onClose }) {
  const video = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        setReady(true);
      } catch (e) {
        setError(e?.name === "NotAllowedError"
          ? "Camera is blocked. Allow the camera for this site in your browser settings, then try again."
          : "Couldn't open the front camera on this phone.");
      }
    })();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      (stream?.getTracks() || []).forEach((t) => t.stop());
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const snap = () => {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const scale = Math.min(1, MAX_SIDE / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    const ctx = c.getContext("2d");
    ctx.translate(c.width, 0);      // mirror, like the preview
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, c.width, c.height);
    onResult(c.toDataURL("image/jpeg", 0.7));
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col" role="dialog" aria-modal="true" aria-label={title}
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="flex items-center justify-between p-4">
        <div>
          <div className="text-white font-semibold">{title}</div>
          {hint && <div className="text-xs text-zinc-400">{hint}</div>}
        </div>
        <button type="button" aria-label="Close" onClick={onClose} className="p-2 text-zinc-300"><X className="h-7 w-7" /></button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={video} playsInline muted className="absolute inset-0 h-full w-full object-cover -scale-x-100" />
        {error && <div className="absolute inset-x-4 bottom-8 rounded-lg bg-rose-600/90 p-3 text-sm text-white">{error}</div>}
      </div>
      <div className="p-6 flex justify-center">
        <button type="button" onClick={snap} disabled={!ready} aria-label="Take selfie" data-testid="take-selfie"
          className="h-16 w-16 rounded-full bg-white disabled:opacity-40 flex items-center justify-center">
          <Camera className="h-7 w-7 text-black" />
        </button>
      </div>
    </div>
  );
}
